/**
 * O pedido de senha de um PDF protegido, com o nome do arquivo à vista.
 *
 * Papel: mostra qual arquivo está pedindo senha, um campo de senha que não
 * sugere nem guarda nada, e os botões "Abrir" e "Pular". Quem monta é o
 * `ModalDeImportacao`, que o põe no lugar da escolha de tipo enquanto houver
 * arquivo protegido na fila.
 *
 * Como usar: a senha vive só no estado deste componente. Ao mandar, ela sai
 * para `onAbrir` e o campo é limpo, então um erro de senha pede de novo com o
 * campo vazio. Quem monta deve manter a `key` igual ao identificador do arquivo,
 * para que um arquivo novo nunca herde a senha digitada para o anterior. O
 * `motivo` `incorreta` mostra o aviso de senha que não abre o arquivo; nenhuma
 * mensagem daqui repete a senha.
 */
import { type FormEvent, useId, useState } from 'react';

import { AvisoDeConferencia } from './kit/aviso-de-conferencia';
import { Botao } from './kit/botao';
import { CLASSE_DO_CONTROLE, Campo } from './kit/campo';

interface PedidoDeSenhaDoPdfProps {
  /** O nome do arquivo que está pedindo senha, como o usuário o escolheu. */
  nomeDoArquivo: string;
  /** `necessaria` na primeira pergunta; `incorreta` depois de uma senha que não serviu. */
  motivo: 'necessaria' | 'incorreta';
  /** Verdadeiro enquanto o arquivo é aberto; desabilita os dois botões. */
  ocupado: boolean;
  /** Recebe a senha digitada. Não vai para o armazenamento nem para o registro. */
  onAbrir: (senha: string) => void;
  /** Descarta só este arquivo e segue com o próximo da fila. */
  onPular: () => void;
}

/**
 * O formulário de senha do arquivo.
 *
 * @param nomeDoArquivo - O nome do arquivo que pede a senha.
 * @param motivo - Se é a primeira pergunta ou a repetição depois de senha errada.
 * @param ocupado - Se a abertura do arquivo está em curso.
 * @param onAbrir - Recebe a senha digitada, depois de limpar o campo.
 * @param onPular - Descarta o arquivo.
 * @returns O formulário, sem caixa própria, para caber no corpo do modal.
 */
export function PedidoDeSenhaDoPdf({
  nomeDoArquivo,
  motivo,
  ocupado,
  onAbrir,
  onPular,
}: PedidoDeSenhaDoPdfProps) {
  const [senha, setSenha] = useState('');
  const idDoCampo = useId();

  /**
   * Manda a senha digitada e limpa o campo antes de devolvê-la.
   *
   * @param evento - O envio do formulário.
   */
  function handleEnviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    if (senha === '' || ocupado) return;
    const digitada = senha;
    setSenha('');
    onAbrir(digitada);
  }

  return (
    <form onSubmit={handleEnviar} className="flex flex-col gap-2">
      <p className="mb-1 text-[12px] text-foreground">
        Este PDF está protegido por senha: <strong>{nomeDoArquivo}</strong>
      </p>
      {motivo === 'incorreta' && (
        <AvisoDeConferencia>
          A senha informada não abre este PDF. Tente de novo.
        </AvisoDeConferencia>
      )}
      <Campo rotulo={`Senha de ${nomeDoArquivo}`} id={idDoCampo}>
        <input
          id={idDoCampo}
          type="password"
          autoComplete="off"
          value={senha}
          onChange={(evento) => setSenha(evento.target.value)}
          className={CLASSE_DO_CONTROLE}
        />
      </Campo>
      <div className="flex justify-end gap-2">
        <Botao variante="fantasma" onClick={onPular} disabled={ocupado}>
          Pular
        </Botao>
        <Botao type="submit" disabled={ocupado || senha === ''}>
          Abrir
        </Botao>
      </div>
    </form>
  );
}
